import { useEffect } from "react";
import { Flex, Box, useColorMode, chakra } from "@chakra-ui/react";
import IconAsButton from "src/components/IconAsButton";
import { useFrame } from "src/components/Frame/Context";
import { Activities } from "src/modules/activity-log/activity";
import StopButton from "src/modules/station/components/StopButton";
import { ReactComponent as ActivityLogIcon } from "src/icons/activitylog.svg";
import { ReactComponent as TabOverlay } from "../icons/tab-clip-path.svg";
import Menu from "./Menu";
import { DarkMode } from "src/styling/Modes";

const EnhancedTabOverlay = chakra(TabOverlay);

export default function ControllingPanel({
  error,
  mode,
  menu,
  stopMonitoring,
  toggleActivityLog,
  isActivityLogOpened,
  activity,
  toggleMute,
  muted,
  jid,
  lullabyPlayer,
  isBabyStationFamilyDevice,
  senderStationConfiguration,
  togglePurchaseFlow,
  shouldRotateCamera,
  openEveryNoise,
  openSettings,
  openLullabyPlayer,
  changeCamera,
  openCameraList,
  detachWindow,
  isInDetachMode,
  mediaCapturer,
  enableDetach,
  frameStyle
}) {
  const { colorMode } = useColorMode();
  const isDarkMode = colorMode === "dark";
  const frame = useFrame();
  const renderControllingIcons = error == null;

  useEffect(() => {
    if (!frame) return;
    updateFrame();
  });

  function updateFrame() {
    frame.update({
      content: controllingPanel,
      containerCss: error
        ? {}
        : mode === "video"
        ? {
            position: "absolute",
            top: "0px",
            left: "0px",
            borderBottom: "solid 1px",
            borderColor: "iconBackground"
          }
        : activity === Activities.SLEEPING && !isInDetachMode
        ? {
            borderBottom: "solid 1px",
            borderColor: "iconBackground"
          }
        : {}
    });
  }
  const controllingPanel = isInDetachMode ? null : (
    <>
      <DarkMode active={mode === "video"} w="100%">
        {mode === "video" && frameStyle?.frameStyles && (
          <EnhancedTabOverlay
            position="fixed"
            top="0"
            left="0"
            zIndex={-1}
            sx={{
              ".tab-selector": {
                x: frameStyle.frameStyles.x + "px",
                width: frameStyle.frameStyles.width + "px"
              }
            }}
          />
        )}
        <Flex
          justifyContent={renderControllingIcons ? "space-between" : "flex-end"}
          alignItems="center"
          width="100%"
          height="38px"
        >
          {renderControllingIcons && (
            <Flex alignItems="center">
              <Menu
                mode={mode}
                isOpen={menu.isOpen}
                onOpen={menu.open}
                onClose={menu.close}
                toggleMute={toggleMute}
                muted={muted}
                enableDetach={enableDetach}
                enableLullabies={
                  lullabyPlayer &&
                  isBabyStationFamilyDevice({ jid }) &&
                  senderStationConfiguration.supportedFeatures.includes(
                    "LULLABIES"
                  )
                }
                openSettings={openSettings}
                openEveryNoise={openEveryNoise}
                openLullabyPlayer={openLullabyPlayer}
                changeCamera={changeCamera}
                canRecordVideo={senderStationConfiguration.supportedFeatures.includes(
                  "VIDEO_RECORDING"
                )}
                canTakeSnapshot={senderStationConfiguration.supportedFeatures.includes(
                  "VIDEO_SNAPSHOT"
                )}
                canChangeCamera={
                  senderStationConfiguration.availableCameras.length > 1
                }
                startRecording={mediaCapturer.startRecording}
                takeSnapshot={mediaCapturer.takeSnapshot}
                togglePurchaseFlow={togglePurchaseFlow}
                openCameraList={openCameraList}
                shouldRotateCamera={shouldRotateCamera}
                detach={detachWindow}
              />
              <IconAsButton
                onClick={toggleActivityLog}
                isActive={isActivityLogOpened}
                ml="24px"
                fill={mode === "video" ? "white" : "mediumEmphasis"}
                icon={ActivityLogIcon}
                size="20px"
              />
            </Flex>
          )}
          <StopButton onClick={stopMonitoring} />
        </Flex>
      </DarkMode>
    </>
  );

  return frame ? null : (
    <Box
      bg={mode === "video" ? "rgba(0,0,0,0.2)" : "transparent"}
      width="100%"
      px="14px"
      {...(mode === "video"
        ? {
            zIndex: 10,
            position: "absolute",
            left: "0px",
            width: "100%",
            borderBottom: "solid 1px",
            borderColor: "iconBackground"
          }
        : {
            borderBottom: "solid 1px",
            borderColor: isDarkMode
              ? "rgba(255, 255, 255, 0.12)"
              : "rgba(0, 0, 0, 0.08)"
          })}
    >
      {controllingPanel}
    </Box>
  );
}
